// Edit a family member: name, relationship to baby, and adult height.
// Owners can edit anyone; everyone can edit themselves; others see read-only.
// Heights feed the Target-Height formula on /growth.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RELATIONSHIP_LABELS } from '@shared/schemas/family.ts';
import type { FamilyMember, Relationship } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow, SegRow } from './SheetShell.tsx';
import { api } from '../../api/client.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  member: FamilyMember | null;
  /** True when the current viewer is the family owner. */
  isOwner: boolean;
  /** True when the member being edited is the current viewer. */
  isSelf: boolean;
  onSaved?: () => void;
};

export function MemberEditSheet({ open, onClose, member, isOwner, isSelf, onSaved }: Props) {
  const [name, setName] = useState<string>('');
  const [relationship, setRelationship] = useState<Relationship | 'none'>('none');
  const [heightCm, setHeightCm] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !member) return;
    setName(member.name);
    setRelationship(member.relationship ?? 'none');
    setHeightCm(member.height_mm != null ? (member.height_mm / 10).toFixed(0) : '');
    setSaving(false);
  }, [open, member?.id]);

  if (!member) return null;
  const m = member;
  const canEdit = isOwner || isSelf;

  async function save() {
    if (!canEdit) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};

      const trimmedName = name.trim();
      if (trimmedName && trimmedName !== m.name) patch.name = trimmedName;

      const newRel: Relationship | null = relationship === 'none' ? null : relationship;
      if (newRel !== (m.relationship ?? null)) patch.relationship = newRel;

      const cm = Number(heightCm.replace(',', '.'));
      const newMm = heightCm.trim() === '' ? null : Number.isFinite(cm) ? Math.round(cm * 10) : null;
      if (newMm !== (m.height_mm ?? null)) patch.height_mm = newMm;

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      // Self editing own name/height: hit /api/me so the user row updates
      // are scoped to the caller; owner editing someone else goes through
      // the family members route.
      if (isSelf && !('relationship' in patch)) {
        await api('/api/me', { method: 'PATCH', json: patch });
      } else {
        await api(`/api/family/members/${m.id}`, { method: 'PATCH', json: patch });
      }
      toast.success('Saved');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={member.name}
      tagline={isSelf ? 'You' : member.relationship ? RELATIONSHIP_LABELS[member.relationship] : 'Family member'}
      accent="accent"
      icon={(p) => <Icon.user {...p} />}
      primaryLabel={canEdit ? (saving ? 'Saving…' : 'Save') : 'Done'}
      primaryDisabled={saving}
      secondaryLabel="Cancel"
      onPrimary={save}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {canEdit ? (
          <FieldRow
            label="Name"
            value={
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                className="lt-tabular"
                style={{ flex: 1, fontSize: 16, color: 'var(--text-1)', textAlign: 'right', minWidth: 0 }}
              />
            }
          />
        ) : (
          <FieldRow label="Name" value={member.name} />
        )}

        {canEdit ? (
          <SegRow
            label="Relationship to baby"
            active={relationship}
            onChange={setRelationship}
            options={(
              [
                'biological_mother',
                'biological_father',
                'partner',
                'caregiver',
                'grandparent',
                'other',
                'none',
              ] as const
            ).map((id) => ({ id, label: RELATIONSHIP_LABELS[id] }))}
          />
        ) : (
          <FieldRow
            label="Relationship to baby"
            value={member.relationship ? RELATIONSHIP_LABELS[member.relationship] : 'Unspecified'}
          />
        )}

        {canEdit ? (
          <FieldRow
            label={isSelf ? 'Your adult height' : `${m.name}'s adult height`}
            value={
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <input
                  inputMode="decimal"
                  pattern="[0-9.,]*"
                  placeholder="—"
                  value={heightCm}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9.,]/g, '').replace(/[.,]+/g, '.');
                    setHeightCm(cleaned);
                  }}
                  className="lt-tabular"
                  style={{ width: 80, fontSize: 18, color: 'var(--text-1)' }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-3)' }}>cm</span>
              </div>
            }
            hint="Used to predict baby's adult height. Optional."
          />
        ) : member.height_mm != null ? (
          <FieldRow label="Adult height" value={`${(member.height_mm / 10).toFixed(0)} cm`} />
        ) : null}

        {!canEdit && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '4px 4px 0' }}>
            Only the family owner or this person can edit these fields.
          </div>
        )}
      </div>
    </SheetShell>
  );
}
